import { useEffect, useRef, useState, useCallback } from 'react';
import { connectSSE, disconnectSSE } from '../services/notifications.service';

const TOKEN_KEY = 'token'; // Must match auth.service.ts TOKEN_KEY

export interface AdminNotification {
    id: string;
    event: string;
    message: string;
    prenom?: string;
    nom?: string;
    adhesionId?: string;
    timestamp: number;
}

interface UseAdminNotificationsReturn {
    notifications: AdminNotification[];
    unreadCount: number;
    clearNotifications: () => void;
    markAllRead: () => void;
}

export function useAdminNotifications(): UseAdminNotificationsReturn {
    const [notifications, setNotifications] = useState<AdminNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const esRef = useRef<EventSource | null>(null);

    const addNotification = useCallback((notification: Omit<AdminNotification, 'id' | 'timestamp'>) => {
        const newNotif: AdminNotification = {
            ...notification,
            id: `${Date.now()}-${Math.random()}`,
            timestamp: Date.now(),
        };
        setNotifications(prev => [newNotif, ...prev].slice(0, 20)); // cap at 20
        setUnreadCount(prev => prev + 1);
    }, []);

    useEffect(() => {
        const token = localStorage.getItem(TOKEN_KEY);
        if (!token) return;

        const es = connectSSE(token);
        esRef.current = es;

        es.addEventListener('connected', () => {
            console.log('[SSE] Connected to admin notification stream');
        });

        es.addEventListener('new-adhesion', (event: MessageEvent) => {
            try {
                const data = JSON.parse(event.data);
                addNotification({
                    event: 'new-adhesion',
                    message: data.message || 'Nouvelle demande d\'adhésion reçue',
                    prenom: data.prenom,
                    nom: data.nom,
                    adhesionId: data.id,
                });
            } catch (err) {
                console.error('[SSE] Failed to parse new-adhesion event', err);
            }
        });

        es.onerror = () => {
            // EventSource reconnects automatically on error — no manual retry needed
            console.warn('[SSE] Connection lost, browser will auto-reconnect...');
        };

        return () => {
            disconnectSSE(esRef.current);
            esRef.current = null;
        };
    }, [addNotification]);

    const clearNotifications = useCallback(() => {
        setNotifications([]);
        setUnreadCount(0);
    }, []);

    const markAllRead = useCallback(() => {
        setUnreadCount(0);
    }, []);

    return { notifications, unreadCount, clearNotifications, markAllRead };
}
